<template>
  <div class="generation-history" ref="root">
    <button
      type="button"
      class="generation-history__trigger"
      :class="{ 'is-open': isOpen }"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click.stop="toggle"
    >
      <i class="fa fa-file-text-o generation-history__trigger-icon" aria-hidden="true"></i>
      <span class="generation-history__trigger-label">{{ $t('generation_history.button') }}</span>
      <i class="fa fa-chevron-down generation-history__trigger-chevron" aria-hidden="true"></i>
    </button>

    <div
      v-show="isOpen"
      ref="panel"
      class="generation-history__panel fade"
      :class="{ in: fadeIn }"
      role="menu"
      :style="panelStyle"
      @click.stop
    >
      <div
        class="generation-history__panel-arrow"
        aria-hidden="true"
        :style="arrowStyle"
      ></div>
      <div class="generation-history__columns">
        <nav class="generation-history__nav" aria-label="generation-history-nav">
          <button type="button" class="generation-history__nav-item" @click.prevent="onOpenAllHistory">
            <span class="generation-history__nav-icon generation-history__nav-icon--history">
              <i class="fa fa-clock-o" aria-hidden="true"></i>
            </span>
            <span class="generation-history__nav-label">{{ $t('generation_history.history') }}</span>
            <i class="fa fa-chevron-right generation-history__nav-chevron" aria-hidden="true"></i>
          </button>
          <button type="button" class="generation-history__nav-item" @click.prevent="onOpenFavoritesList">
            <span class="generation-history__nav-icon generation-history__nav-icon--favorites">
              <i class="icon-star icon-star-o" aria-hidden="true"></i>
            </span>
            <span class="generation-history__nav-label">{{ $t('generation_history.favorites_list') }}</span>
            <i class="fa fa-chevron-right generation-history__nav-chevron" aria-hidden="true"></i>
          </button>
        </nav>

        <div class="generation-history__quick">
          <h3 class="generation-history__quick-title">{{ $t('generation_history.quick_access') }}</h3>
          <div class="generation-history__search">
            <div class="generation-history__search-field">
              <i class="fa fa-search generation-history__search-icon" aria-hidden="true"></i>
              <input
                type="text"
                class="generation-history__search-input"
                :class="{ 'has-error': keywordError }"
                :placeholder="$t('generation_history.keyword_placeholder')"
                :value="keyword"
                @input="onKeywordInput"
                @keydown.enter.prevent="onKeywordSearch"
              >
              <p v-if="keywordError" class="generation-history__search-error">{{ keywordError }}</p>
            </div>
            <button type="button" class="generation-history__search-btn" @click.prevent="onKeywordSearch">
              {{ $t('generation_history.search') }}
            </button>
          </div>
          <div class="generation-history__shortcuts">
            <div class="generation-history__shortcuts-row generation-history__shortcuts-row--recent">
              <button type="button" class="generation-history__shortcut" @click.prevent="onOpenRecentGenerations">
                <i class="fa fa-clock-o" aria-hidden="true"></i>
                <span>{{ $t('generation_history.recent_generations') }}</span>
              </button>
              <button type="button" class="generation-history__shortcut" @click.prevent="onOpenRecentFavorites">
                <i class="icon-star" aria-hidden="true"></i>
                <span>{{ $t('generation_history.recent_favorites') }}</span>
              </button>
            </div>
            <div class="generation-history__shortcuts-row generation-history__shortcuts-row--period">
              <button type="button" class="generation-history__shortcut" @click.prevent="onOpenToday">
                <i class="icon-calendar"></i>
                <span>{{ $t('generation_history.today') }}</span>
              </button>
              <button type="button" class="generation-history__shortcut" @click.prevent="onOpenThisWeek">
                <i class="icon-calendar"></i>
                <span>{{ $t('generation_history.this_week') }}</span>
              </button>
              <button type="button" class="generation-history__shortcut" @click.prevent="onOpenThisMonth">
                <i class="icon-calendar"></i>
                <span>{{ $t('generation_history.this_month') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import constants from '@/shared/constants/constants';

const PANEL_RIGHT_OFFSET = 64;
const PANEL_TOP_OVERLAP = 8;
const ARROW_SIZE = 14;
// bootstrap.css `.fade` → transition: opacity .15s linear
const BOOTSTRAP_FADE_MS = 150;

export default {
  name: 'GeneratedHistoryPopup',
  data() {
    return {
      isOpen: false,
      fadeIn: false,
      keyword: '',
      keywordError: '',
      panelStyle: {},
      arrowStyle: {},
      hideTimer: null,
    };
  },
  watch: {
    isOpen(open) {
      if (open) {
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
        window.addEventListener('resize', this.updatePanelPosition);
      } else {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        window.removeEventListener('resize', this.updatePanelPosition);
      }
    },
  },
  beforeDestroy() {
    this.clearHideTimer();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    window.removeEventListener('resize', this.updatePanelPosition);
  },
  methods: {
    ...mapActions('generationHistory', ['openSidebar']),
    openHistoryFilter(filter, keyword = '', tab = 'history') {
      this.close();
      this.$nextTick(() => {
        this.openSidebar({ filter, keyword, tab });
      });
      this.keyword = '';
      this.keywordError = '';
    },
    onOpenAllHistory() {
      this.openHistoryFilter(constants.GENERATION_HISTORY_FILTER_ALL);
    },
    onOpenFavoritesList() {
      this.openHistoryFilter(
        constants.GENERATION_HISTORY_FILTER_ALL,
        '',
        'favorites',
      );
    },
    onKeywordInput(event) {
      this.keyword = event.target.value;
      if (this.keywordError) {
        this.keywordError = '';
      }
    },
    onKeywordSearch() {
      if (!this.keyword) {
        this.keywordError = this.$t('generation_history.search_keyword_required');
        return;
      }
      const trimmed = (this.keyword || '').trim();
      if (trimmed.length > constants.GENERATION_HISTORY_KEYWORD_MAX_LENGTH) {
        this.keywordError = this.$t('generation_history.keyword_max_length_error', {
          max: constants.GENERATION_HISTORY_KEYWORD_MAX_LENGTH,
        });
        return;
      }
      this.keywordError = '';
      this.openHistoryFilter(constants.GENERATION_HISTORY_FILTER_KEYWORD, trimmed);
    },
    onOpenRecentGenerations() {
      this.openHistoryFilter(constants.GENERATION_HISTORY_FILTER_RECENT_30D);
    },
    onOpenRecentFavorites() {
      this.openHistoryFilter(
        constants.GENERATION_HISTORY_FILTER_RECENT_30D,
        '',
        'favorites',
      );
    },
    onOpenToday() {
      this.openHistoryFilter(constants.GENERATION_HISTORY_FILTER_TODAY);
    },
    onOpenThisWeek() {
      this.openHistoryFilter(constants.GENERATION_HISTORY_FILTER_THIS_WEEK);
    },
    onOpenThisMonth() {
      this.openHistoryFilter(constants.GENERATION_HISTORY_FILTER_THIS_MONTH);
    },
    toggle() {
      if (this.isOpen && !this.hideTimer) {
        this.close();
      } else {
        this.open();
      }
    },
    open() {
      this.clearHideTimer();
      this.updatePanelPosition();
      this.isOpen = true;
      this.fadeIn = false;
      this.$nextTick(() => {
        const panel = this.$refs.panel;
        if (panel) {
          void panel.offsetWidth;
        }
        this.fadeIn = true;
      });
    },
    close() {
      if (!this.isOpen) {
        return;
      }
      this.fadeIn = false;
      this.clearHideTimer();
      // Wait for Bootstrap `.fade` opacity transition before v-show hides
      this.hideTimer = setTimeout(() => {
        this.isOpen = false;
        this.hideTimer = null;
      }, BOOTSTRAP_FADE_MS);
    },
    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },
    updatePanelPosition() {
      const root = this.$refs.root;
      if (!root) {
        return;
      }
      const rect = root.getBoundingClientRect();
      const buttonCenterFromRight = window.innerWidth - (rect.left + rect.width / 2);
      this.panelStyle = {
        top: `${rect.bottom - PANEL_TOP_OVERLAP}px`,
        right: `${PANEL_RIGHT_OFFSET}px`,
      };
      this.arrowStyle = {
        right: `${buttonCenterFromRight - PANEL_RIGHT_OFFSET - ARROW_SIZE / 2}px`,
      };
    },
    onDocumentClick(event) {
      const root = this.$refs.root;
      if (root && !root.contains(event.target)) {
        this.close();
      }
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        this.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.generation-history {
  position: relative;
  display: flex;
  align-items: center;
  height: 95px;
  margin: 0 8px;
}

.generation-history__trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background: #e3f0fb;
  color: #1a3a5c;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.95;
  }
  &.is-open {
    background: #e3f0fb;
  }
}

.generation-history__trigger-icon {
  font-size: 14px;
  color: #2f6fa8;
}

.generation-history__trigger-chevron {
  font-size: 10px;
  color: #4a6a88;
}

.generation-history__panel {
  position: fixed;
  left: auto;
  z-index: 1100;
  min-width: 600px;
  padding-top: 10px;
}

.generation-history__panel-arrow {
  position: absolute;
  top: 2px;
  left: auto;
  width: 14px;
  height: 14px;
  background: #fff;
  border-left: 1px solid #d9e2ec;
  border-top: 1px solid #d9e2ec;
  transform: rotate(45deg);
  z-index: 1;
}

.generation-history__columns {
  display: flex;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(20, 40, 70, 0.12);
  overflow: hidden;
  position: relative;
  z-index: 2;
}

.generation-history__nav {
  width: 230px;
  padding: 12px 0;
  border-right: 1px solid #e8eef4;
}

.generation-history__nav-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 24px 14px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: #243447;

  &:hover {
    background: #eef6fb;
  }

  & + & {
    border-top: 1px solid #edf2f7;
  }
}

.generation-history__nav-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  margin-right: 10px;
  flex-shrink: 0;

  i {
    font-size: 16px;
  }

  &--history {
    background: #e3f0fb;
    color: #2f7cc0;
  }

  &--favorites {
    background: #fff0e0;
    color: #e08a2c;
  }
}

.generation-history__nav-label {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
}

.generation-history__nav-chevron {
  font-size: 11px;
  color: #8aa0b5;
}

.generation-history__quick {
  flex: 1;
  padding: 16px 18px 18px;
  min-width: 340px;
}

.generation-history__quick-title {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 700;
  color: #1f3348;
}

.generation-history__search {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 0;
  padding-bottom: 20px;
  border-bottom: 1px solid #e8eef4;
}

.generation-history__search-field {
  position: relative;
  flex: 1;
  min-width: 0;
}

.generation-history__search-icon {
  position: absolute;
  left: 10px;
  top: 18px;
  transform: translateY(-50%);
  color: #8aa0b5;
  font-size: 16px;
}

.generation-history__search-input {
  width: 100%;
  height: 36px;
  padding: 0 10px 0 34px;
  border: 1px solid #cfd9e4;
  border-radius: 6px;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #6aa8d8;
  }

  &.has-error {
    border-color: #e74c3c;
  }
}

.generation-history__search-error {
  margin: 6px 0 0;
  font-size: 12px;
  color: #e74c3c;
}

.generation-history__search-btn {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #5b9fd4;
  border-radius: 6px;
  background: #e3f0fb;
  color: #2f7cc0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f3f9fd;
  }
}

.generation-history__shortcuts {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 20px;
}

.generation-history__shortcuts-row {
  display: grid;
  gap: 14px;

  &--recent {
    grid-template-columns: 1fr 1fr;
  }

  &--period {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.generation-history__shortcut {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #d5dee8;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #eef6fb;
    border-color: #b7c9da;
  }
}

i {
  color: #5f7f9c;
  width: 16px;
  font-size: 16px;
  text-align: center;

  &.icon-calendar {
    display: inline-block;
    width: 16px;
    height: 16px;
    background-color: currentColor;
    mask: url('../../assets/img/common/date.png') no-repeat center / contain;
    -webkit-mask: url('../../assets/img/common/date.png') no-repeat center / contain;
  }

  &.icon-star {
    display: inline-block;
    width: 24px;
    height: 24px;
    background-color: currentColor;
    mask: url('../../assets/img/common/icon-star.svg') no-repeat center / contain;
    -webkit-mask: url('../../assets/img/common/icon-star.svg') no-repeat center / contain;
  }
  &.icon-star-o {
    color: #e08a2c;
  }
}
</style>
